/**
 * Per-pump business insights — select a pump, then run each check with its own button.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import {
  fetchAdminBusinessInsightsDateDefaults,
  fetchAdminBusinessInsightsModule,
  fetchPumpNames,
  friendlyFetchError,
} from './adminApi';
import {
  BUSINESS_INSIGHT_MODULES,
  BusinessInsightModuleResult,
} from './adminBusinessInsightShared';
import AdminPumpReportExportButton from './AdminPumpReportExportButton';
import './admin-dashboard.css';

const BUSINESS_INSIGHTS_EXPORT_ID = 'admin-business-insights-page-export';

const AdminBusinessInsights = (props) => {
  const history = useHistory();
  const [pumps, setPumps] = useState([]);
  const [pumpId, setPumpId] = useState('');
  const [pumpName, setPumpName] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [moduleState, setModuleState] = useState({});
  const [loadingPumps, setLoadingPumps] = useState(true);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (!profile) {
          history.goBack();
          return;
        }
        fetchPumpNames()
          .then((res) => {
            setPumps(res.pump_detail || []);
          })
          .catch(console.error)
          .finally(() => setLoadingPumps(false));
      })
      .catch(() => history.goBack());
  }, [history]);

  const loadPumpDates = useCallback((id) => {
    if (!id) {
      setFromDate('');
      setToDate('');
      return;
    }
    fetchAdminBusinessInsightsDateDefaults(id)
      .then((defaults) => {
        if (defaults.status === false) return;
        setFromDate(defaults.from_date || '');
        setToDate(defaults.to_date || '');
        setPumpName(defaults.pump_name || '');
      })
      .catch(console.error);
  }, []);

  const handlePumpChange = (e) => {
    const id = e.target.value;
    setPumpId(id);
    setModuleState({});
    const selected = pumps.find((p) => String(p.id) === String(id));
    setPumpName(selected?.pump_name || '');
    loadPumpDates(id);
  };

  const runModule = (moduleKey, extra = {}) => {
    if (!pumpId) return;

    setModuleState((prev) => ({
      ...prev,
      [moduleKey]: { ...prev[moduleKey], loading: true, error: null },
    }));

    fetchAdminBusinessInsightsModule(pumpId, moduleKey, fromDate, toDate, extra)
      .then((data) => {
        if (data.status === false) {
          throw new Error(data.message || 'Check failed');
        }
        setModuleState((prev) => ({
          ...prev,
          [moduleKey]: { loading: false, error: null, data, loadedAt: new Date().toISOString() },
        }));
      })
      .catch((err) => {
        setModuleState((prev) => ({
          ...prev,
          [moduleKey]: { loading: false, error: friendlyFetchError(err), data: null },
        }));
      });
  };

  const canRun = Boolean(pumpId && fromDate && toDate);

  return (
    <div className="main-container page-wrap admin-platform">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={31} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Business Insights" />
          <div className="container pb-4 mt-3">
            <Link to="/admin_dashboard" className="small">← Control Center</Link>

            <div className="admin-panel mt-3 admin-bi-intro">
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                <div>
                  <h5 className="mb-2">Pump business insights</h5>
                  <p className="text-muted mb-0">
                    Select one pump, set the date range, then run each check separately — one button per test.
                  </p>
                </div>
                <AdminPumpReportExportButton
                  targetId={BUSINESS_INSIGHTS_EXPORT_ID}
                  filename={pumpId ? `pump-${pumpId}-business-insights.pdf` : 'business-insights.pdf'}
                  label="Download PDF"
                  className="btn btn-sm btn-outline-primary"
                  disabled={!pumpId || !fromDate || !toDate}
                  metadata={{
                    title: pumpName ? `${pumpName} — Business Insights` : 'Business Insights',
                    subtitle: fromDate && toDate ? `${fromDate} to ${toDate}` : '',
                  }}
                  message="Capturing all visible insight modules and charts. Run the checks first for a full report."
                />
              </div>

              <div className="row g-2 align-items-end admin-bi-date-filter">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Pump *</label>
                  <select
                    className="form-select form-select-sm"
                    value={pumpId}
                    onChange={handlePumpChange}
                    disabled={loadingPumps}
                  >
                    <option value="">— Select pump —</option>
                    {pumps.map((p) => (
                      <option key={p.id} value={p.id}>{p.pump_name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">From date</label>
                  <input type="date" className="form-control form-control-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={!pumpId} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">To date</label>
                  <input type="date" className="form-control form-control-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={!pumpId} />
                </div>
                <div className="col-md-4">
                  {pumpId ? (
                    <p className="small mb-0">
                      Checking: <strong>{pumpName}</strong>
                      {' · '}
                      <Link to={`/admin-pump/${pumpId}`} className="small">Open pump hub</Link>
                    </p>
                  ) : (
                    <p className="small text-warning mb-0">Select a pump to enable the checks below.</p>
                  )}
                </div>
              </div>
            </div>

            {!pumpId && (
              <div className="admin-panel mt-3 text-center py-4 text-muted">
                Choose a pump above to start running insight checks.
              </div>
            )}

            {pumpId && (
              <div id={BUSINESS_INSIGHTS_EXPORT_ID}>
            {BUSINESS_INSIGHT_MODULES.map((mod) => {
              const state = moduleState[mod.key] || {};
              return (
                <div key={mod.key} className="admin-panel mt-3 admin-bi-module">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <h6 className="mb-1">{mod.label}</h6>
                      <p className="small text-muted mb-0">{mod.description}</p>
                      {mod.slow && <p className="small text-warning mb-0">May take 30–60 seconds.</p>}
                    </div>
                    <button
                      type="button"
                      className={`btn btn-sm btn-${mod.tone}`}
                      disabled={!canRun || state.loading}
                      onClick={() => runModule(mod.key, mod.key === 'expense-anomalies' ? { months: 12 } : {})}
                    >
                      {state.loading ? 'Running…' : 'Run check'}
                    </button>
                  </div>

                  {state.error && <div className="alert alert-danger py-2 small mb-2">{state.error}</div>}

                  {state.data && (
                    <div className="admin-bi-result">
                      <div className="small text-muted mb-2">
                        {pumpName} · {state.data.from_date} to {state.data.to_date}
                        {state.loadedAt ? ` · ${new Date(state.loadedAt).toLocaleString('en-IN')}` : ''}
                      </div>
                      <BusinessInsightModuleResult moduleKey={mod.key} data={state.data} />
                    </div>
                  )}

                  {!state.data && !state.loading && !state.error && (
                    <p className="small text-muted mb-0">Not run yet — click &quot;Run check&quot; to test this module.</p>
                  )}
                </div>
              );
            })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBusinessInsights;
